import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
    CalendarIcon, Plus, X, Trash2, Save, Flame, 
    TrendingUp, Activity, AlertTriangle
} from 'lucide-react';
import AttachmentExpander from '../components/AttachmentExpander';
import useAttachmentCounts from '../hooks/useAttachmentCounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMMON_SYMPTOMS = [
    "Fatigue", "Joint Pain", "Muscle Pain", "Brain Fog", "Headache",
    "Nausea", "Dizziness", "Skin Rash", "Swelling", "Stiffness",
    "Digestive Issues", "Fever", "Numbness/Tingling"
];

const COMMON_TRIGGERS = [
    "Stress", "Poor Sleep", "Food Reaction", "Weather Change", "Infection",
    "Overexertion", "Missed Medication", "Hormonal Changes", "Unknown"
];

const getSeverityColor = (severity) => {
    if (severity <= 3) return 'bg-amber-500';
    if (severity <= 6) return 'bg-orange-500';
    return 'bg-red-500';
};

const getSeverityBgColor = (severity) => {
    if (severity <= 3) return 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
    if (severity <= 6) return 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800';
    return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
};

export default function FlareJournal() {
    const { token } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    
    // Form state
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [severity, setSeverity] = useState(5);
    const [symptoms, setSymptoms] = useState([]);
    const [triggers, setTriggers] = useState([]);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/flares?limit=50`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setEntries(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch entries:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const toggleSymptom = (symptom) => {
        if (symptoms.includes(symptom)) {
            setSymptoms(symptoms.filter(s => s !== symptom));
        } else {
            setSymptoms([...symptoms, symptom]);
        }
    };

    const toggleTrigger = (trigger) => {
        if (triggers.includes(trigger)) {
            setTriggers(triggers.filter(t => t !== trigger));
        } else {
            setTriggers([...triggers, trigger]);
        }
    };

    const saveFlare = async () => {
        if (symptoms.length === 0) {
            toast.error('Please select at least one symptom');
            return;
        }

        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/flares`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    severity,
                    symptoms,
                    triggers: triggers.length > 0 ? triggers : null,
                    notes: notes || null
                })
            });

            if (res.ok) {
                toast.success('Flare recorded');
                setShowAdd(false);
                setSeverity(5);
                setSymptoms([]);
                setTriggers([]);
                setNotes('');
                fetchEntries();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save flare');
        } finally {
            setSaving(false);
        }
    };

    // Calculate stats
    const thisMonthFlares = entries.filter(e => {
        const date = parseISO(e.created_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    });

    const avgSeverity = thisMonthFlares.length > 0 
        ? (thisMonthFlares.reduce((sum, e) => sum + e.severity, 0) / thisMonthFlares.length).toFixed(1)
        : null;

    // Find most common symptoms
    const symptomCounts = {};
    entries.forEach(e => {
        e.symptoms.forEach(s => {
            symptomCounts[s] = (symptomCounts[s] || 0) + 1;
        });
    });
    const topSymptoms = Object.entries(symptomCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    // Attachment counts (paperclip badges)
    const attachmentCounts = useAttachmentCounts('flare_journal', entries.map(e => e.id));

    // Find most common triggers
    const triggerCounts = {};
    entries.forEach(e => {
        (e.triggers || []).forEach(t => {
            triggerCounts[t] = (triggerCounts[t] || 0) + 1;
        });
    });
    const topTriggers = Object.entries(triggerCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Flare Journal</h1>
                    <p className="text-muted-foreground mt-1">Track and analyze your flare patterns</p>
                </div>
                <Button onClick={() => setShowAdd(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="log-flare-btn">
                    <Flame className="w-4 h-4 mr-2" />
                    Log Flare
                </Button>
            </div>

            {entries.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/20 border-orange-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-500 rounded-lg">
                                    <Flame className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">This Month</p>
                                    <p className="text-xl font-bold">{thisMonthFlares.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={`border ${avgSeverity ? getSeverityBgColor(parseFloat(avgSeverity)) : ''}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${avgSeverity ? getSeverityColor(parseFloat(avgSeverity)) : 'bg-gray-400'}`}>
                                    <TrendingUp className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Avg Severity</p>
                                    <p className="text-xl font-bold">{avgSeverity || '-'}/10</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/30">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-500 rounded-lg">
                                    <Activity className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Total Logged</p>
                                    <p className="text-xl font-bold">{entries.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {topSymptoms.length > 0 && (
                        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/20">
                            <CardContent className="p-4">
                                <p className="text-xs text-muted-foreground">Top Symptom</p>
                                <p className="font-bold truncate">{topSymptoms[0][0]}</p>
                                <p className="text-xs text-muted-foreground">{topSymptoms[0][1]} occurrences</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Add Flare Form */}
                    {showAdd && (
                        <Card className="border-2 border-orange-200 dark:border-orange-800">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Flame className="w-5 h-5 text-orange-500" />
                                        Log a Flare
                                    </CardTitle>
                                    <Button variant="ghost" size="icon" onClick={() => setShowAdd(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Severity */}
                                <div className={`p-4 rounded-xl border ${getSeverityBgColor(severity)}`}>
                                    <label className="text-sm font-medium mb-3 block">Flare Severity</label>
                                    <div className="flex items-center gap-4">
                                        <input
                                            type="range"
                                            min="1"
                                            max="10"
                                            value={severity}
                                            onChange={(e) => setSeverity(parseInt(e.target.value))}
                                            className="flex-1 h-3 rounded-lg appearance-none cursor-pointer accent-orange-600"
                                            data-testid="severity-slider"
                                        />
                                        <Badge className={`${getSeverityColor(severity)} text-lg px-3 py-1`}>
                                            {severity}/10
                                        </Badge>
                                    </div>
                                </div>

                                {/* Symptoms */}
                                <div>
                                    <label className="text-sm font-medium mb-3 block">Symptoms ({symptoms.length} selected)</label>
                                    <div className="flex flex-wrap gap-2">
                                        {COMMON_SYMPTOMS.map((symptom) => (
                                            <Badge
                                                key={symptom}
                                                variant={symptoms.includes(symptom) ? "default" : "outline"}
                                                className="cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900 transition-colors"
                                                onClick={() => toggleSymptom(symptom)}
                                                data-testid={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`}
                                            >
                                                {symptom}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Triggers */}
                                <div>
                                    <label className="text-sm font-medium mb-3 block">Possible Triggers</label>
                                    <div className="flex flex-wrap gap-2">
                                        {COMMON_TRIGGERS.map((trigger) => (
                                            <Badge
                                                key={trigger}
                                                variant={triggers.includes(trigger) ? "default" : "outline"}
                                                className="cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
                                                onClick={() => toggleTrigger(trigger)}
                                            >
                                                {trigger}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Notes */}
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Notes</label>
                                    <Textarea
                                        placeholder="Describe the flare, what helped, how long it lasted..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={4}
                                        data-testid="flare-notes-input"
                                    />
                                </div>

                                <Button 
                                    onClick={saveFlare} 
                                    disabled={saving || symptoms.length === 0}
                                    className="w-full bg-orange-600 hover:bg-orange-700"
                                    data-testid="save-flare-btn"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Saving...' : 'Save Flare Entry'}
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Flare History */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Flare History</CardTitle>
                            <CardDescription>Your recorded flare events</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                                </div>
                            ) : entries.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Flame className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No flares logged yet</p>
                                    <p className="text-sm">Tracking flares helps identify patterns</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {entries.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={`p-4 rounded-xl border ${getSeverityBgColor(entry.severity)}`}
                                            data-testid={`flare-entry-${entry.id}`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <Badge className={getSeverityColor(entry.severity)}>
                                                        {entry.severity}/10
                                                    </Badge>
                                                    <span className="text-sm text-muted-foreground">
                                                        {format(parseISO(entry.created_at), 'MMM d, yyyy h:mm a')}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="mb-2">
                                                <p className="text-xs text-muted-foreground mb-1">Symptoms:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {entry.symptoms.map((s, idx) => (
                                                        <Badge key={idx} variant="outline" className="text-xs">{s}</Badge>
                                                    ))}
                                                </div>
                                            </div>

                                            {entry.triggers && entry.triggers.length > 0 && (
                                                <div className="mb-2">
                                                    <p className="text-xs text-muted-foreground mb-1">Triggers:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {entry.triggers.map((t, idx) => (
                                                            <Badge key={idx} variant="secondary" className="text-xs">{t}</Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {entry.notes && (
                                                <p className="text-sm mt-2 p-2 bg-white/50 dark:bg-slate-800/50 rounded">{entry.notes}</p>
                                            )}
                                            <AttachmentExpander
                                                parentType="flare_journal"
                                                parentId={entry.id}
                                                initialCount={attachmentCounts[`flare_journal:${entry.id}`] || 0}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    {/* Top Symptoms */}
                    {topSymptoms.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Top Symptoms</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {topSymptoms.map(([symptom, count]) => (
                                        <div key={symptom} className="flex items-center justify-between">
                                            <span className="text-sm">{symptom}</span>
                                            <Badge variant="secondary">{count}x</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Top Triggers */}
                    {topTriggers.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    Common Triggers
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {topTriggers.map(([trigger, count]) => (
                                        <div key={trigger} className="flex items-center justify-between">
                                            <span className="text-sm">{trigger}</span>
                                            <Badge variant="secondary">{count}x</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">🔥 Flare Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Log flares as soon as possible</p>
                            <p>• Note what you were doing before</p>
                            <p>• Track what helps you recover</p>
                            <p>• Look for patterns over time</p>
                            <p>• Share insights with your doctor</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
